import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { SvgIconProps } from '@mui/material';
import { SvgIcon } from '@mui/material';
import { APP_STORE_URL, PLAY_STORE_URL } from '../../config/stores';
import { nav } from '../../theme';

// The two store badges.
//
// They were an MUI contained button next to an MUI outlined one, which read
// as a primary action and a secondary action rather than as two equal ways
// to get the same app. These are one shape, one colour, one size — the
// convention every store badge follows, and the reason the real ones from
// Apple and Google look like this.
//
// Drawn rather than using the official badge images: those are locked
// artwork with their own clear-space and colour rules, they come in a
// dozen locales, and two PNGs that must not be recoloured would fight the
// rest of this page. The shape is the convention, not their trademark.

// MUI ships no Apple glyph, and the Play "shop bag" is not the Play mark.
// Both are drawn here so the two badges are actually the two stores.
const AppleGlyph: React.FC<SvgIconProps> = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </SvgIcon>
);

const PlayGlyph: React.FC<SvgIconProps> = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path d="M3.6 2.3c-.3.3-.5.7-.5 1.3v16.8c0 .6.2 1 .5 1.3l.1.1 9.4-9.4v-.2L3.7 2.2l-.1.1zM16.3 15.1l-3.1-3.1v-.2l3.1-3.1.1.1 3.7 2.1c1.1.6 1.1 1.6 0 2.2l-3.7 2.1-.1-.1zM15.6 15.9L12.4 12.7 3 22.1c.4.4 1 .4 1.7.1l10.9-6.3M15.6 8.1L4.7 1.8C4 1.4 3.4 1.5 3 1.9l9.4 9.4 3.2-3.2z" />
  </SvgIcon>
);

const Badge: React.FC<{
  href: string;
  icon: React.ReactNode;
  small: string;
  large: string;
}> = ({ href, icon, small, large }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noreferrer"
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 1.5,
      px: 2.25, py: 1.15, borderRadius: 2.5,
      bgcolor: nav.bg, color: '#fff', textDecoration: 'none',
      // A fixed width, not a minimum — the two labels are different
      // lengths, and badges of slightly different sizes look like an
      // accident rather than a pair.
      width: { xs: '100%', sm: 200 },
      transition: 'transform 160ms ease, box-shadow 160ms ease',
      '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 8px 22px rgba(22,21,15,0.22)' },
      '& svg': { fontSize: 26 },
    }}
  >
    {icon}
    <Box sx={{ lineHeight: 1.15 }}>
      <Typography component="span" sx={{ display: 'block', fontSize: 11, opacity: 0.75 }}>
        {small}
      </Typography>
      <Typography component="span" sx={{ display: 'block', fontSize: 15.5, fontWeight: 700 }}>
        {large}
      </Typography>
    </Box>
  </Box>
);

const StoreButtons: React.FC = () => (
  <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
    {/* Only the stores we can actually link to — see config/stores.ts. */}
    {APP_STORE_URL && (
      <Badge href={APP_STORE_URL} icon={<AppleGlyph />} small="Download on the" large="App Store" />
    )}
    <Badge href={PLAY_STORE_URL} icon={<PlayGlyph />} small="Get it on" large="Google Play" />
  </Stack>
);

export default StoreButtons;
